import type { ReactNode } from "react";
import { SidebarProvider, SidebarTrigger } from "@/shared/components/ui/sidebar";
import { AppSidebar } from "@/shared/components/AppSidebar";
import { ActingViewBanner } from "@/shared/components/ActingViewBanner";
import { usePageTitle } from "@/shared/hooks/usePageTitle";
import { useUserPreferences } from "@/shared/hooks/useUserPreferences";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { useTheme } from "@/shared/hooks/useTheme";

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  usePageTitle();
  const { preferences, setSidebarPreference } = useUserPreferences();
  const isMobile = useIsMobile();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Derive sidebar state directly from preferences
  const sidebarOpen = preferences.sidebarPreference === "expanded";

  const handleSidebarChange = (open: boolean) => {
    // Persist the preference - this will update preferences which will re-render
    setSidebarPreference(open ? "expanded" : "collapsed");
  };

  return (
    <SidebarProvider
      defaultOpen={sidebarOpen}
      open={sidebarOpen}
      onOpenChange={handleSidebarChange}
    >
      <div className="h-svh print:h-auto flex w-full bg-background">
        <AppSidebar />

        <div className="flex-1 flex flex-col min-w-0">
          <ActingViewBanner />
          {/* Mobile header with menu trigger */}
          {isMobile && (
            <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background px-4 md:hidden">
              <SidebarTrigger />
              <img
                src={isDark ? "/logo-invexis-neon.png" : "/logo-invexis-dark.png"}
                alt="Invexis"
                className="h-8 w-auto"
              />
            </header>
          )}

          {/* Main content */}
          <main className="flex-1 p-6 overflow-auto print:overflow-visible">
            <div className="max-w-none w-full h-full">
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}